import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, onSnapshot, updateDoc, increment, setDoc, getDoc, arrayUnion, collection, runTransaction, addDoc, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';
import { useReferralSettings } from '../hooks/useReferralSettings';
import { useServicesStatus } from '../hooks/useServicesStatus';

interface Investment {
  amount: number;
  days: number;
  remainingDays: number;
  type: 'collective' | 'other';
  date: Date;
  isActive: boolean;
}

// @ts-ignore -- À utiliser plus tard
interface Donation {
  requestId: string;
  amount: number;
  isPublic: boolean;
  timestamp: Date;
}

interface WithdrawInfo {
  phoneNumber: string;
  fullName: string;
}

interface WalletContextType {
  balance: number;
  updateBalance: (amount: number, requestId?: string) => Promise<boolean>;
  handleCollectiveInvestment: (amount: number) => Promise<void>;
  referralCount: number;
  referralEarnings: number;
  referralCode: string;
  investments: Investment[];
  updateInvestments: (amount: number) => Promise<boolean>;
  handleReferralReward: (referrerId: string) => Promise<boolean>;
  referralSettings: any;
  requestWithdraw: (amount: number, withdrawInfo: WithdrawInfo) => Promise<boolean>;
  handleAnonymousDonation: (amount: number, requestId: string, recipientId: string, displayName?: string) => Promise<boolean>;
}

const WalletContext = createContext<WalletContextType | undefined>(undefined);

export function WalletProvider({ children }: { children: React.ReactNode }) {
  const auth = useAuth();

  if (!auth) {
    return null;
  }

  const { user } = auth;
  const [balance, setBalance] = useState(0);
  const [referralCount, setReferralCount] = useState(0);
  const [referralEarnings, setReferralEarnings] = useState(0);
  const [referralCode, setReferralCode] = useState('');
  const [investments, setInvestments] = useState<Investment[]>([]);
  const { settings: referralSettings } = useReferralSettings();
  const { status: servicesStatus } = useServicesStatus();

  useEffect(() => {
    if (!user?.id) return;

    const walletRef = doc(db, 'wallets', user.id);
    const userRef = doc(db, 'users', user.id);
    
    // Vérifier et créer le portefeuille si nécessaire
    const initializeWallet = async () => {
      try {
        console.log(`Vérification du portefeuille pour l'utilisateur ${user.id}...`);
        const walletDoc = await getDoc(walletRef);
        
        if (!walletDoc.exists()) {
          console.log(`Création d'un nouveau portefeuille pour l'utilisateur ${user.id}...`);
          await setDoc(walletRef, {
            balance: 0,
            investments: [],
            createdAt: new Date()
          });
          console.log(`Portefeuille créé avec succès pour l'utilisateur ${user.id}`);
        } else {
          console.log(`Portefeuille existant trouvé pour l'utilisateur ${user.id}`);
        }
      } catch (error) {
        console.error(`Erreur lors de l'initialisation du portefeuille:`, error);
        // Tentative de récupération après erreur
        setTimeout(() => {
          console.log("Tentative de réinitialisation du portefeuille...");
          initializeWallet().catch(e => console.error("Échec de la réinitialisation:", e));
        }, 2000);
      }
    };

    // Exécuter l'initialisation et gérer les erreurs
    initializeWallet()
      .catch(error => {
        console.error("Erreur non gérée lors de l'initialisation du portefeuille:", error);
      });

    // Écouter les changements du portefeuille (uniquement pour le solde et les investissements)
    const walletUnsubscribe = onSnapshot(walletRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        setBalance(data.balance || 0);
        setInvestments(data.investments || []);
      } else {
        console.log("Le document du portefeuille n'existe pas dans le listener. Tentative de réinitialisation...");
        initializeWallet().catch(error => console.error("Erreur lors de la réinitialisation:", error));
      }
    }, error => {
      console.error("Erreur d'écoute du portefeuille:", error);
    });

    // Écouter les changements des informations de parrainage dans la collection users
    const userUnsubscribe = onSnapshot(userRef, (doc) => {
      if (doc.exists()) {
        const userData = doc.data();
        setReferralCount(userData.referralCount || 0);
        setReferralEarnings(userData.referralEarnings || 0);
        setReferralCode(userData.referralCode || '');
      }
    }, error => {
      console.error("Erreur d'écoute des données utilisateur:", error);
    });

    return () => {
      walletUnsubscribe();
      userUnsubscribe();
    };
  }, [user?.id]);

  const updateBalance = async (amount: number, requestId?: string): Promise<boolean> => {
    if (!user?.id) return false;

    // Vérifier si le service est actif
    if (amount > 0 && !servicesStatus?.deposits.active) {
      throw new Error(servicesStatus?.deposits.message || 'Service de recharge désactivé');
    }

    if (amount < 0 && !servicesStatus?.withdrawals.active) {
      throw new Error(servicesStatus?.withdrawals.message || 'Service de retrait désactivé');
    }

    try {
      // Utiliser runTransaction pour s'assurer que toutes les lectures sont effectuées avant les écritures
      return await runTransaction(db, async (transaction) => {
        // 1. DÉFINIR TOUTES LES RÉFÉRENCES
        const walletRef = doc(db, 'wallets', user.id);
        const userRef = doc(db, 'users', user.id);
        const requestRef = requestId ? doc(db, '2dr_requests', requestId) : null;
        const statsRef = doc(db, 'settings', 'financialStats');
        
        // 2. EFFECTUER TOUTES LES LECTURES D'ABORD
        const walletDoc = await transaction.get(walletRef);
        const statsDoc = await transaction.get(statsRef);
        const currentBalance = walletDoc.exists() ? walletDoc.data()?.balance || 0 : 0;
        
        // Lecture supplémentaire du document utilisateur si nécessaire
        let userDoc = null;
        if (amount < 0 && requestRef) {
          userDoc = await transaction.get(userRef);
        }
        
        // Lire la requête si nécessaire (donation)
        let requestDoc = null;
        if (amount < 0 && requestRef) {
          requestDoc = await transaction.get(requestRef);
        }
        
        // Vérifier le solde
        if (amount < 0 && currentBalance + amount < 0) {
          throw new Error('Solde insuffisant');
        }
        
        // 3. APRÈS TOUTES LES LECTURES, EFFECTUER LES ÉCRITURES
        
        // Mettre à jour le portefeuille
        if (walletDoc.exists()) {
          transaction.update(walletRef, {
            balance: increment(amount)
          });
        } else {
          transaction.set(walletRef, {
            balance: amount,
            referralCount: 0,
            referralEarnings: 0,
            referralCode: user.id.slice(0, 6).toUpperCase(),
            investments: [],
            createdAt: new Date()
          });
        }
        
        // Si c'est une donation (montant négatif), mettre à jour amountGiven
        if (amount < 0 && requestRef && userDoc) {
          // Mettre à jour le total des donations de l'utilisateur
          transaction.update(userRef, {
            amountGiven: increment(Math.abs(amount))
          });
          
          // Ajouter la donation à la requête si la requête existe
          if (requestDoc && requestDoc.exists()) {
            transaction.update(requestRef, {
              donations: arrayUnion({
                userId: user.id,
                amount: Math.abs(amount),
                date: new Date(),
                isAnonymous: false
              })
            });
          }
        }
        
        // Mettre à jour les statistiques globales
        if (statsDoc.exists()) {
          transaction.update(statsRef, {
            totalTransactions: increment(Math.abs(amount)),
            totalBalance: increment(amount),
            lastUpdated: new Date()
          });
        } else {
          transaction.set(statsRef, {
            totalTransactions: Math.abs(amount),
            totalBalance: amount,
            lastUpdated: new Date()
          });
        }
        
        return true;
      }).then(success => {
        // Après la transaction, créer l'entrée d'historique séparément
        if (success) {
          const transactionRef = doc(collection(db, 'users', user.id, 'transactions'));
          setDoc(transactionRef, {
            type: requestId ? 'donation' : 'wallet_update',
            amount: amount,
            requestId: requestId || null,
            date: new Date(),
            status: 'completed'
          });
        }
        return success;
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      return false;
    }
  };

  const handleReferralReward = async (referrerId: string): Promise<boolean> => {
    if (!referralSettings?.isActive) return false;

    try {
      const referrerWalletRef = doc(db, 'wallets', referrerId);
      const referrerDoc = await getDoc(referrerWalletRef);

      if (referrerDoc.exists()) {
        const referrerData = referrerDoc.data();
        if (referrerData.referralCount >= referralSettings.maxReferrals) {
          return false;
        }

        await updateDoc(referrerWalletRef, {
          balance: increment(referralSettings.reward),
          referralCount: increment(1),
          referralEarnings: increment(referralSettings.reward)
        });

        return true;
      }
      return false;
    } catch (error) {
      console.error('Erreur lors du traitement de la récompense de parrainage:', error);
      return false;
    }
  };

  // Fonction spécifique pour les investissements collectifs
  const handleCollectiveInvestment = async (amount: number) => {
    if (!user) return;
    
    try {
      const walletRef = doc(db, 'wallets', user.id);
      const transactionRef = doc(db, 'transactions', user.id);

      // Vérifier si le document de transactions existe
      const transactionDoc = await getDoc(transactionRef);

      // Créer la nouvelle transaction
      const transaction = {
        type: 'collective_investment',
        amount: -amount,
        date: new Date(),
        status: 'completed',
        title: 'Donations collectives' // Ajout d'un titre pour l'affichage
      };

      if (!transactionDoc.exists()) {
        // Créer le document avec un tableau initial
        await setDoc(transactionRef, {
          history: [transaction]
        });
      } else {
        // Ajouter à l'historique existant
        await updateDoc(transactionRef, {
          history: arrayUnion(transaction)
        });
      }

      // Mettre à jour le solde du portefeuille
      await updateDoc(walletRef, {
        balance: increment(-amount)
      });

    } catch (error) {
      console.error('Erreur lors de l\'investissement collectif:', error);
      throw error;
    }
  };

  // Nouvelle fonction pour les demandes de retrait
  const requestWithdraw = async (amount: number, withdrawInfo: WithdrawInfo): Promise<boolean> => {
    if (!user?.id) return false;

    try {
      // Vérifier d'abord les demandes de retrait en cours pour cet utilisateur
      const pendingWithdrawalsQuery = query(
        collection(db, 'withdrawRequests'),
        where('userId', '==', user.id),
        where('status', '==', 'pending')
      );
      
      const pendingWithdrawalsSnapshot = await getDocs(pendingWithdrawalsQuery);
      let totalPendingAmount = 0;
      
      // Calculer le montant total des retraits en attente
      pendingWithdrawalsSnapshot.forEach(doc => {
        totalPendingAmount += doc.data().amount;
      });
      
      // Utiliser runTransaction pour garantir l'atomicité des opérations
      return await runTransaction(db, async (transaction) => {
        // Références
        const walletRef = doc(db, 'wallets', user.id);
        const statsRef = doc(db, 'settings', 'financialStats');
        const withdrawRequestRef = doc(db, 'withdrawRequests', `${user.id}_${Date.now()}`);
        const userRef = doc(db, 'users', user.id);
        const transactionRef = doc(collection(db, 'users', user.id, 'transactions'));
        
        // Lire le solde actuel du portefeuille
        const walletDoc = await transaction.get(walletRef);
        if (!walletDoc.exists()) {
          throw new Error('Portefeuille introuvable');
        }
        
        const currentBalance = walletDoc.data().balance || 0;
        
        // Vérifier si le solde est suffisant en tenant compte du minimum de 2000 FCFA
        if (currentBalance - totalPendingAmount - amount < 2000) {
          throw new Error('Solde insuffisant : un minimum de 2000 FCFA doit rester sur le portefeuille');
        }
        
        // Lire les statistiques
        const statsDoc = await transaction.get(statsRef);
        
        // Mettre à jour le solde du portefeuille IMMÉDIATEMENT
        transaction.update(walletRef, {
          balance: increment(-amount)
        });
        
        // Sauvegarder les informations de retrait
        transaction.update(userRef, {
          withdrawInfo: withdrawInfo
        });

        // Créer la demande de retrait
        transaction.set(withdrawRequestRef, {
          userId: user.id,
          email: user.email,
          amount: amount,
          phoneNumber: withdrawInfo.phoneNumber,
          fullName: withdrawInfo.fullName,
          country: user.country || 'Non spécifié',
          status: 'pending',
          createdAt: new Date()
        });

        // Ajouter la transaction à l'historique
        transaction.set(transactionRef, {
          type: 'withdrawal_request',
          amount: -amount,
          status: 'pending',
          date: new Date(),
          title: 'Demande en cours...',
          withdrawalId: withdrawRequestRef.id,
          withdrawInfo: withdrawInfo
        });

        // Mettre à jour les statistiques
        if (statsDoc.exists()) {
          transaction.update(statsRef, {
            totalWithdrawRequests: increment(amount),
            totalBalance: increment(-amount),
            lastUpdated: new Date()
          });
        } else {
          transaction.set(statsRef, {
            totalWithdrawRequests: amount,
            totalBalance: -amount,
            lastUpdated: new Date()
          });
        }
        
        return true;
      });
    } catch (error) {
      console.error('Erreur lors de la demande de retrait:', error);
      return false;
    }
  };

  const handleAnonymousDonation = async (amount: number, requestId: string, recipientId: string, displayName?: string): Promise<boolean> => {
    try {
      // Ajouter d'abord la donation à la collection
      const requestRef = doc(db, '2dr_requests', requestId);
      
      // Puis exécuter la transaction pour mettre à jour le portefeuille
      return await runTransaction(db, async (transaction) => {
        // 1. Définir toutes les références
        const recipientWalletRef = doc(db, 'wallets', recipientId);
        const statsRef = doc(db, 'settings', 'financialStats');
        const requestDocRef = doc(db, '2dr_requests', requestId);

        // 2. Effectuer toutes les lectures d'abord
        const recipientWalletDoc = await transaction.get(recipientWalletRef);
        const statsDoc = await transaction.get(statsRef);
        const requestDoc = await transaction.get(requestDocRef);

        // 3. Après toutes les lectures, effectuer les écritures
        
        // Mettre à jour le portefeuille du destinataire
        if (recipientWalletDoc.exists()) {
          transaction.update(recipientWalletRef, {
            balance: increment(amount)
          });
        } else {
          transaction.set(recipientWalletRef, {
            balance: amount,
            referralCount: 0,
            referralEarnings: 0,
            referralCode: recipientId.slice(0, 6).toUpperCase(),
            investments: [],
            createdAt: new Date()
          });
        }

        // Mettre à jour les statistiques globales
        if (statsDoc.exists()) {
          transaction.update(statsRef, {
            totalTransactions: increment(amount),
            totalBalance: increment(amount),
            lastUpdated: new Date()
          });
        } else {
          transaction.set(statsRef, {
            totalTransactions: amount,
            totalBalance: amount,
            lastUpdated: new Date()
          });
        }

        return true;
      }).then(success => {
        // Après la transaction réussie, ajouter la donation au document
        if (success) {
          const donationsRef = collection(requestRef, 'donations');
          addDoc(donationsRef, {
            amount,
            date: new Date(),
            isAnonymous: true,
            displayName: displayName || null,
            timestamp: serverTimestamp()
          });
        }
        return success;
      });
    } catch (error) {
      console.error('Erreur lors du don anonyme:', error);
      return false;
    }
  };

  return (
    <WalletContext.Provider value={{
      balance,
      updateBalance,
      handleCollectiveInvestment,
      referralCount,
      referralEarnings,
      referralCode,
      investments,
      updateInvestments: updateBalance,
      handleReferralReward,
      referralSettings,
      requestWithdraw,
      handleAnonymousDonation
    }}>
      {children}
    </WalletContext.Provider>
  );
}

export function useWallet() {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within WalletProvider');
  }
  return context;
}